import { useEffect } from 'react';

import { isError } from '@game/utils';
import { getAuthInstance } from '@lib/authInstance';
import { cleanupTabSessionCoordinator, initializeTabSessionCoordinator } from '@lib/tabSessionCoordinator';
import type { ReactNode } from 'react';

import { authStore } from '@store/authStore';
import { cleanupAuthSubscriptions, initializeAuthSubscriptions } from '@store/authSubscriptions';

import { AuthContext } from './AuthContext';

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps): ReactNode {
  const auth = authStore((s) => s.auth);
  const { handleError } = authStore.getState();

  useEffect(() => {
    async function loadAuth(): Promise<void> {
      try {
        const authInstance = await getAuthInstance();
        authStore.setState({ auth: authInstance });
        initializeAuthSubscriptions(authInstance);
        initializeTabSessionCoordinator();
      } catch (error) {
        if (isError(error)) {
          handleError(error);
        }
      }
    }

    void loadAuth();
    // Cleanup
    return (): void => {
      const authInstance = authStore.getState().auth;
      authInstance?.unsubscribe();
      cleanupAuthSubscriptions();
      cleanupTabSessionCoordinator();
    };
  }, [handleError]);

  if (!auth) {
    // Auth not yet initialized
    return null;
  }

  const logOut = (): void => {
    // Flag this tab as the logout initiator so that the Firebase onAuthStateChanged(null)
    // event — which propagates to all tabs — is only acted on here, not in sibling tabs.
    sessionStorage.setItem('bw-tab-logout', '1');
    authStore.getState().handleLoggingOut();
    auth.signOut().catch((error: unknown) => {
      if (isError(error)) {
        handleError(error);
      }
    });
  };

  return <AuthContext.Provider value={{ auth, logOut }}>{children}</AuthContext.Provider>;
}
